import React, { useState } from 'react';
import { useSprings, animated } from 'react-spring';
import styled from 'styled-components';
import DropsAndWinsStamp from './DropsAndWins';

const ThumbnailWrapper = styled.div`
  will-change: transform;
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 2px;
  overflow: hidden;
`;

const GameThumbnail = styled.div`
  will-change: transform;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 2px;
`;

export const BackgroundOverlay = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }): string => theme.blue14};
  opacity: 0.4;
  transition: opacity 0.35s ease-in-out;
`;

const GameLogo = styled(animated.div)<{ src: string }>`
  position: absolute;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border: none;
  margin: 0 auto;
  padding: 0;
  align-self: center;
  background-image: ${({ src }) => `url(${src}?w=150)`};

  @media screen and (min-width: 768px) {
    background-image: ${({ src }) => `url(${src}?w=200)`};
  }
`;

const GameLogoWrapper = styled.div(({ theme }) => ({
  display: 'flex',
  position: 'absolute',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  bottom: 0,
  width: '100%',
  height: '100%',

  [GameLogo]: {
    width: '60%',
    height: '60%',
  },

  [theme.breakpoints.up('lg')]: {
    [GameLogo]: {
      width: '70%',
      height: '70%',
    },
  },
}));

const BackgroundImage = styled(animated.div)<{ src: string }>`
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  width: 100%;
  height: 100%;
  background-image: ${({ src }) => `url(${src}?w=200)`};

  @media screen and (min-width: 768px) {
    background-image: ${({ src }) => `url(${src}?w=250)`};
  }
`;

const Flag = styled.span`
  position: absolute;
  top: 12px;
  left: -4px;
  opacity: 0.9;
  font-size: 10px;
  font-weight: ${({ theme }): number => theme.medium};
  letter-spacing: 0.4px;
  line-height: 11px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 2px;
  padding: 0 4px;
  z-index: 1;
`;

const GameFlag = styled(Flag)`
  top: 4px;
  left: 4px;
  color: ${({ theme }): string => theme.white};
  background-color: ${({ theme }): string => theme.blue15};
`;

type Props = {
  backgroundUrl?: string;
  logoUrl?: string;
  gameFlag?: string;
  dropsAndWins?: boolean;
};

const Thumbnail: React.FunctionComponent<Props> = ({
  backgroundUrl = '',
  logoUrl = '',
  dropsAndWins,
  gameFlag,
}) => {
  const [animate, setAnimate] = useState(false);

  const handleHover = (): void => {
    setAnimate(!animate);
  };

  const items = [
    {
      // logo style
      scaleFrom: 'scale3d(1, 1, 1)',
      scaleTo: animate ? 'scale3d(1.2, 1.2, 1.2)' : 'scale3d(1, 1, 1)',
    },
    {
      // bgImageStyle style
      scaleFrom: 'scale3d(1.1, 1.1, 1.1)',
      scaleTo: animate ? 'scale3d(1, 1, 1)' : 'scale3d(1.1, 1.1, 1.1)',
    },
  ];

  const [logoStyle, bgImageStyle] = useSprings(
    items.length,
    items.map((item) => ({
      from: {
        transform: item.scaleFrom,
      },
      to: {
        transform: item.scaleTo,
      },
      config: { friction: 20, tension: 210, mass: 0.1 },
    }))
  );

  return (
    <ThumbnailWrapper>
      <GameThumbnail onMouseEnter={handleHover} onMouseLeave={handleHover}>
        {gameFlag && !dropsAndWins && <GameFlag>{gameFlag}</GameFlag>}
        {dropsAndWins && (
          <Flag>
            <DropsAndWinsStamp />
          </Flag>
        )}
        <BackgroundImage style={bgImageStyle} src={backgroundUrl}>
          <BackgroundOverlay />
        </BackgroundImage>
        <GameLogoWrapper>
          <GameLogo style={logoStyle} src={logoUrl}></GameLogo>
        </GameLogoWrapper>
      </GameThumbnail>
    </ThumbnailWrapper>
  );
};

export default Thumbnail;
